import { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Loader2, ShieldAlert, ShieldCheck, ShieldX, Sparkles, FileText, ChevronRight } from "lucide-react";
import { toast } from "sonner";

export default function PlagiarismChecker() {
  const [text, setText] = useState("");
  const [title, setTitle] = useState("");
  const [detectAi, setDetectAi] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (text.trim().length < 20) {
      toast.error("Paste at least 20 characters of text");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const { data } = await api.post("/plagiarism/check", {
        text,
        title: title || null,
        detect_ai: detectAi,
        check_corpus: ["assessments", "documents", "generations"],
      });
      setResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Plagiarism check failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-5" data-testid="plagiarism-panel">
      <div className="lg:col-span-2 tb-card p-6 space-y-4">
        <div>
          <Label>Title (optional)</Label>
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="mt-1.5 h-11 rounded-xl"
            placeholder="Aanya's essay on photosynthesis"
            data-testid="plg-title"
          />
        </div>
        <div>
          <Label>Submission text</Label>
          <Textarea
            rows={12}
            value={text}
            onChange={(e) => setText(e.target.value)}
            className="mt-1.5 rounded-xl"
            placeholder="Paste the student's submission here…"
            data-testid="plg-text"
          />
          <p className="mt-1.5 text-[11px] text-muted-foreground">
            We compare against your saved assessments, OCR documents, and AI generations — privately, in your account.
          </p>
        </div>
        <div className="flex items-center justify-between rounded-2xl border border-border p-3">
          <div>
            <p className="text-sm font-medium">AI-content detection</p>
            <p className="text-[11px] text-muted-foreground">Estimate likelihood of LLM-generated text.</p>
          </div>
          <Switch checked={detectAi} onCheckedChange={setDetectAi} data-testid="plg-detect-ai" />
        </div>
        <Button onClick={submit} disabled={busy} className="w-full h-11 rounded-full" data-testid="plg-submit">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><ShieldAlert className="h-4 w-4 mr-1.5" /> Check</>}
        </Button>
      </div>

      <div className="lg:col-span-3 tb-card p-6 min-h-[480px]" data-testid="plg-result">
        {!result && !busy && (
          <div className="h-full grid place-items-center text-center text-muted-foreground py-16">
            <div>
              <ShieldCheck className="h-8 w-8 mx-auto opacity-50" />
              <p className="mt-3 font-display">Plagiarism & AI-content report will appear here.</p>
            </div>
          </div>
        )}
        {busy && (
          <div className="h-full grid place-items-center text-center text-muted-foreground py-16">
            <div>
              <Loader2 className="h-8 w-8 animate-spin mx-auto" />
              <p className="mt-3 font-display">Comparing against your library…</p>
            </div>
          </div>
        )}
        {result && <Report r={result} />}
      </div>
    </div>
  );
}

function Report({ r }) {
  const VerdictIcon =
    r.verdict === "high" ? ShieldX :
    r.verdict === "suspicious" ? ShieldAlert :
    ShieldCheck;

  const tone = {
    high: "bg-rose-50 border-rose-200 text-rose-800 dark:bg-rose-500/10 dark:border-rose-500/30 dark:text-rose-200",
    suspicious: "bg-amber-50 border-amber-200 text-amber-800 dark:bg-amber-500/10 dark:border-amber-500/30 dark:text-amber-200",
    clean: "bg-emerald-50 border-emerald-200 text-emerald-800 dark:bg-emerald-500/10 dark:border-emerald-500/30 dark:text-emerald-200",
  }[r.verdict];

  const verdictLabel = {
    high: "High overlap detected",
    suspicious: "Suspicious overlap",
    clean: "Looks clean",
  }[r.verdict];

  return (
    <div className="space-y-6">
      <div className={`rounded-2xl border p-5 flex items-center gap-4 ${tone}`} data-testid="plg-verdict">
        <VerdictIcon className="h-7 w-7 shrink-0" />
        <div>
          <p className="font-display text-lg font-medium tracking-tight">{verdictLabel}</p>
          <p className="text-xs opacity-80 mt-1">
            Highest similarity in your library: <span className="font-semibold">{r.similarity_pct}%</span>
            {r.ai_likelihood_pct != null && (
              <> · AI-content likelihood: <span className="font-semibold">{r.ai_likelihood_pct}%</span></>
            )}
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Gauge label="Library similarity" value={r.similarity_pct} />
        <Gauge label="AI likelihood" value={r.ai_likelihood_pct ?? 0} disabled={r.ai_likelihood_pct == null} />
      </div>

      {r.ai_signals?.length > 0 && (
        <div className="rounded-2xl border border-border bg-muted/40 p-4">
          <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground inline-flex items-center gap-1.5">
            <Sparkles className="h-3 w-3" /> AI signals
          </p>
          <ul className="mt-2 list-disc ml-5 text-sm space-y-1">
            {r.ai_signals.map((s, i) => <li key={`sig-${i}-${(s || '').slice(0, 16)}`}>{s}</li>)}
          </ul>
        </div>
      )}

      <div>
        <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">
          Library matches ({r.matches.length})
        </p>
        {r.matches.length === 0 ? (
          <p className="text-sm text-muted-foreground py-4 text-center">No significant matches found in your library.</p>
        ) : (
          <div className="space-y-2">
            {r.matches.map((m, i) => (
              <div key={`${m.source || 'match'}-${i}`} className="rounded-2xl border border-border bg-card p-4" data-testid={`plg-match-${i}`}>
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] font-bold text-muted-foreground">
                    <FileText className="h-3 w-3" /> {m.source}
                  </span>
                  <span className="tb-chip-butter rounded-full text-[11px] px-2 py-0.5 font-medium">
                    {m.similarity}% similar
                  </span>
                </div>
                <p className="font-medium text-sm mt-2">{m.title}</p>
                <p className="text-xs text-muted-foreground line-clamp-3 mt-1">&ldquo;{m.matched_excerpt}&rdquo;</p>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Gauge({ label, value, disabled = false }) {
  const pct = Math.max(0, Math.min(100, value || 0));
  const color =
    pct >= 60 ? "bg-rose-500" :
    pct >= 25 ? "bg-amber-500" :
    "bg-emerald-500";
  return (
    <div className={`rounded-2xl border border-border p-4 ${disabled ? "opacity-60" : ""}`}>
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium">{label}</p>
        <p className="font-display text-lg font-medium tabular-nums">{disabled ? "—" : `${pct}%`}</p>
      </div>
      <div className="mt-2 h-2 rounded-full bg-muted overflow-hidden">
        <div className={`h-full ${color} transition-all`} style={{ width: `${disabled ? 0 : pct}%` }} />
      </div>
    </div>
  );
}
